import { c as _c } from "react/compiler-runtime";
import React from "react";
import { motion } from "framer-motion";
import dynamic from "next/dynamic";
import type { FilePreviewProps } from "../reducers/types";
import VideoFallbackPreview from "./VideoFallbackPreview";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRotate, faCube } from "@fortawesome/free-solid-svg-icons";

// Dynamically import GLB viewer to avoid SSR issues
const MediaDisplayGLB = dynamic(() => import("../../../../drops/view/item/content/media/MediaDisplayGLB"), {
  ssr: false,
  loading: () => <div className="tw-flex tw-h-full tw-items-center tw-justify-center tw-text-iron-400">
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-3">
          <FontAwesomeIcon icon={faCube} className="tw-h-8 tw-w-8 tw-flex-shrink-0" />
          <span>Loading 3D model...</span>
        </div>
      </div>
});

/**
 * File Preview Component
 *
 * Displays a preview of the uploaded file, with specialized handling for images and videos.
 * Includes compatibility checking and fallback for non-playable videos.
 *
 * @param props Component props
 * @returns JSX Element
 */
const FilePreview: React.FC<FilePreviewProps> = t0 => {
  const $ = _c(41);
  const {
    url,
    file,
    mimeType,
    onRemove,
    videoCompatibility,
    isCheckingCompatibility
  } = t0;
  const effectiveMimeType = file?.type ?? mimeType ?? "";
  let t1;
  if ($[0] !== effectiveMimeType || $[1] !== url) {
    t1 = effectiveMimeType.startsWith("video/") || url.startsWith("data:video/");
    $[0] = effectiveMimeType;
    $[1] = url;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const isVideo = t1;
  let t2;
  if ($[3] !== effectiveMimeType || $[4] !== file?.name) {
    t2 = effectiveMimeType === "model/gltf-binary" || effectiveMimeType === "model/gltf+json" || file?.name.toLowerCase().endsWith(".glb") || file?.name.toLowerCase().endsWith(".gltf");
    $[3] = effectiveMimeType;
    $[4] = file?.name;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const isGLB = t2;
  const isHtml = effectiveMimeType === "text/html";
  const canPlayVideo = !isVideo || (videoCompatibility?.canPlay ?? true);
  let t3;
  if ($[6] !== url) {
    t3 = () => <img src={url} alt="Artwork preview" className="tw-absolute tw-max-h-full tw-max-w-full tw-object-contain tw-shadow-lg" />;
    $[6] = url;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const renderImagePreview = t3;
  let t4;
  if ($[8] !== url) {
    t4 = () => <video src={url} className="tw-max-h-full tw-max-w-full tw-object-contain tw-shadow-lg" controls={true} onError={_temp} />;
    $[8] = url;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const renderVideoPreview = t4;
  const renderHtmlPreview = _temp2;
  let t5;
  if ($[10] !== isHtml || $[11] !== isVideo || $[12] !== renderImagePreview || $[13] !== renderVideoPreview || $[14] !== url) {
    t5 = () => {
      if (isHtml) {
        return renderHtmlPreview();
      }
      if (url.startsWith("data:image/")) {
        return renderImagePreview();
      }
      if (isVideo) {
        return renderVideoPreview();
      }
      return renderImagePreview();
    };
    $[10] = isHtml;
    $[11] = isVideo;
    $[12] = renderImagePreview;
    $[13] = renderVideoPreview;
    $[14] = url;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const renderStandardPreview = t5;
  let t6;
  if ($[16] !== canPlayVideo || $[17] !== file || $[18] !== isGLB || $[19] !== isVideo || $[20] !== renderStandardPreview || $[21] !== url || $[22] !== videoCompatibility?.errorMessage) {
    t6 = () => {
      if (isGLB) {
        return <div className="tw-h-full tw-min-h-[300px] tw-w-full"><MediaDisplayGLB src={url} /></div>;
      }
      const shouldShowStandardPreview = !isVideo || canPlayVideo;
      if (shouldShowStandardPreview) {
        return <>{renderStandardPreview()}</>;
      }
      if (file) {
        return <VideoFallbackPreview file={file} errorMessage={videoCompatibility?.errorMessage} />;
      }
      return null;
    };
    $[16] = canPlayVideo;
    $[17] = file;
    $[18] = isGLB;
    $[19] = isVideo;
    $[20] = renderStandardPreview;
    $[21] = url;
    $[22] = videoCompatibility?.errorMessage;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const renderMediaContent = t6;
  let t7;
  let t8;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      opacity: 0
    };
    t8 = {
      opacity: 1
    };
    $[24] = t7;
    $[25] = t8;
  } else {
    t7 = $[24];
    t8 = $[25];
  }
  let t9;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="tw-absolute tw-inset-0 tw-opacity-5"><div className="tw-grid tw-h-full tw-grid-cols-8">{Array(64).fill(0).map(_temp3)}</div></div>;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== isCheckingCompatibility || $[28] !== isVideo) {
    t10 = isCheckingCompatibility && isVideo && <div className="tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-center tw-justify-center tw-bg-iron-950/70"><div className="tw-flex tw-flex-col tw-items-center tw-gap-3"><div className="tw-h-10 tw-w-10 tw-animate-spin tw-rounded-full tw-border-b-2 tw-border-t-2 tw-border-primary-500" /><span className="tw-text-iron-300">Checking video compatibility...</span></div></div>;
    $[27] = isCheckingCompatibility;
    $[28] = isVideo;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== renderMediaContent) {
    t11 = renderMediaContent();
    $[30] = renderMediaContent;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== t11) {
    t12 = <div className="tw-relative tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-overflow-hidden tw-p-4">{t11}</div>;
    $[32] = t11;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <FontAwesomeIcon icon={faArrowsRotate} className="tw-h-3.5 tw-w-3.5 tw-flex-shrink-0" />;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== onRemove) {
    t14 = <div className="tw-absolute tw-right-3 tw-top-3 tw-z-30 tw-flex tw-gap-2"><button onClick={onRemove} className="tw-flex tw-items-center tw-justify-center tw-gap-x-2 tw-rounded-lg tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-800 tw-px-2.5 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-iron-300 tw-shadow-sm tw-ring-1 tw-ring-iron-700 tw-transition tw-duration-300 tw-ease-out hover:tw-border-iron-700 hover:tw-bg-iron-700 hover:tw-ring-iron-650 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-iron-700" aria-label="Change uploaded file" tabIndex={0} data-testid="artwork-replace-button">{t13}Change</button></div>;
    $[35] = onRemove;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== t10 || $[38] !== t12 || $[39] !== t14) {
    t15 = <motion.div initial={t7} animate={t8} className="tw-relative tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-bg-iron-900">{t9}{t10}{t12}{t14}</motion.div>;
    $[37] = t10;
    $[38] = t12;
    $[39] = t14;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  return t15;
};
export default FilePreview;
function _temp(e) {
  console.error("Video playback error:", e);
}
function _temp2() {
  return <div className="tw-flex tw-h-full tw-w-full tw-flex-col tw-items-center tw-justify-center tw-p-6 tw-text-center"><span className="tw-mb-2 tw-text-sm tw-font-semibold tw-text-iron-200">Interactive artwork ready</span><span className="tw-max-w-sm tw-text-xs tw-font-medium tw-text-iron-500">The existing interactive media will be reused unless you change it.</span></div>;
}
function _temp3(_, i) {
  return <div key={i} className={`tw-border-[0.5px] ${i % 2 === 0 ? "tw-bg-iron-400/10" : "tw-bg-iron-600/10"}`} />;
}